import { Box, Divider } from '@mui/material';
import { AddAndUpdatePartyLedgerEntry } from 'components/add-and-update-party-ledger-modal/add-and-update-party-ledger-entry';
import AddLedgerEntry from 'components/add-entry-modal/add-entry-modal';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import React, { FC } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { formatMoney } from 'utils/helpers';
import { AccountingType, Loan } from 'v2-types/order';

import { LoanFormType, ModalData } from '../../types';
import { Additional } from '../additional';
import { CreateInterestModal } from '../create-interest-modal';
import { Flags } from '../flags';
import { Interests } from '../interests';
import { LoanActions } from '../loan-actions';
import { LoanAmountAndAmountKind } from '../loan-amount-and-amount-kind';
import { Property } from '../property';
import { RelatedAmountsAccounting } from '../related-amounts-accounting';
import { TermsAndDatesIncreasesDisclosure } from '../terms-and-dates-increases-disclosure';
import { useStyles } from './styles';
import { kindAllowedAssociationList } from './utils';
import { useTranslation } from 'react-i18next';
import { TermsAndDates } from '../terms-and-dates-increases-disclosure/components/terms-and-dates';
import { HUD_1 } from 'utils/constants';

type Props = {
  isLoansLoading: boolean;
  isUpdateLoanLoading: boolean;
  selectedLoan: Loan | undefined;
  loanForm: UseFormReturn<LoanFormType, object>;
  setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
  modalData: ModalData | undefined;
  onCloseModal: () => void;
  onDeleteLoan: () => void;
  isDeleteLoanLoading: boolean;
  onDeleteLoanInterest: () => void;
  isDeleteInterestLoading: boolean;
  onSave: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  deleteAdditional: () => void;
  isDeleteAdditionalLoading: boolean;
  orderType: string
};

export const LoanContent: FC<Props> = ({
  isLoansLoading,
  isUpdateLoanLoading,
  selectedLoan,
  loanForm,
  setModalData,
  modalData,
  onCloseModal,
  onDeleteLoan,
  onDeleteLoanInterest,
  isDeleteInterestLoading,
  isDeleteLoanLoading,
  onSave,
  deleteAdditional,
  isDeleteAdditionalLoading,
  orderType
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.content}>
      <LoanActions
        isDataLoading={isLoansLoading}
        isUpdateLoading={isUpdateLoanLoading}
        isFormDirty={loanForm.formState.isDirty}
        setModalData={setModalData}
        onSave={onSave}
      />
      <Box className={classes.container}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: '40px', flex: 1 }}>
          <Box className={classes.rowPrimary}>
            <Box className={classes.rowPrimaryLeft}>
              <LoanAmountAndAmountKind
                isDataLoading={isLoansLoading}
                loanForm={loanForm}
                onSave={onSave}
              />
              <Property isDataLoading={isLoansLoading} selectedLoan={selectedLoan} onSave={onSave} loanForm={loanForm} />
              <Flags loanForm={loanForm} onSave={onSave} />
            </Box>
            <Box className={classes.rowPrimaryRight}>

              <TermsAndDates
                loanForm={loanForm}
                isDataLoading={isLoansLoading}
                orderType={orderType}
                onSave={onSave}
              />
            </Box>
          </Box>
        </Box>

        <Divider orientation="vertical" className={classes.dividerVertical} />

        <Box className={classes.rowSecondary}>
          <Box className={classes.rowSecondaryLeft}>

            <RelatedAmountsAccounting
              isDataLoading={isLoansLoading}
              selectedLoan={selectedLoan}
              setModalData={setModalData}
            />
          </Box>

          <Box className={classes.rowSecondaryRight}>
            <Box className={classes.nestedRow}>
              <Box className={classes.nestedRowItem}>
                <Interests
                  isDataLoading={isLoansLoading}
                  isUpdateLoading={isUpdateLoanLoading}
                  setModalData={setModalData}
                  selectedLoan={selectedLoan}
                  orderType={orderType}
                />
              </Box>
              <Box className={classes.nestedRowItem}>
                <Additional
                  setModalData={setModalData}
                  isDataLoading={isLoansLoading}
                  isUpdateLoading={isUpdateLoanLoading}
                  loanAmountEntry={loanForm.getValues('loanAmountEntry')}
                />
              </Box>

            </Box>
            {orderType !== HUD_1 &&
              <TermsAndDatesIncreasesDisclosure
                loanForm={loanForm}
                isDataLoading={isLoansLoading}
                onSave={onSave}
              />}

          </Box>
        </Box>
      </Box>
      {modalData?.modal === 'editEntry' && (
        <AddLedgerEntry
          open
          handleClose={onCloseModal}
          prevData={modalData.entry as AccountingType}
          isEditingEntry
          disableFields={{ description: true }}
          hideFields={{
            code: true,
            letter: true,
            kind: true,
            number: true,
            amount: true,
            annuality: true,
            entryDate: true,
            months: true,
            months_advanced: true,
          }}
        />
      )}
      {modalData?.modal === 'deleteInterest' && (
        <DeleteConfirmationModal
          isOpen
          title={t('loan:deleteInterestTitle')}
          confirmationMessage={t('loan:deleteInterestConfirmation')}
          onClose={onCloseModal}
          onConfirm={onDeleteLoanInterest}
          isRemoveLoading={isDeleteInterestLoading}
        />
      )}
      {modalData?.modal === 'deleteLoan' && (
        <DeleteConfirmationModal
          isOpen
          title={t('loan:delete-loan')}
          confirmationMessage={t('loan:deleteLoanConfirmation')}
          onClose={onCloseModal}
          onConfirm={onDeleteLoan}
          isRemoveLoading={isDeleteLoanLoading}
        />
      )}
      {modalData?.modal === 'createInterest' && (
        <CreateInterestModal
          onClose={onCloseModal}
          orderType={orderType}
          loanId={selectedLoan?._id}
        />
      )}
      {(modalData?.modal === 'editPartyEntry' || modalData?.modal === 'createAdditional') && (
        <AddAndUpdatePartyLedgerEntry
          handleClose={onCloseModal}
          kindAllowedAssociationList={kindAllowedAssociationList}
          partyKindFilter={['Buyer']}
          entryLedger={loanForm.getValues('loanAmountEntry') as AccountingType}
          partyLedgerEntry={modalData.entry}
          hideFields={{
            at_closing_amount: true,
            at_closing_percent: true,
            before_closing_amount: true,
            before_closing_percent: true,
          }}
        />
      )}
      {modalData?.modal === 'deleteAdditional' && (
        <DeleteConfirmationModal
          isOpen
          title={t('loan:deleteAdditionalTitle')}
          confirmationMessage={`${t('loan:deleteAdditionalConfirmation')}
          \n The additional with the description "${modalData.entry?.kind
            }" with the amount of ${formatMoney(modalData.entry?.amount || 0)} will be deleted`}
          onClose={onCloseModal}
          onConfirm={() => deleteAdditional()}
          isRemoveLoading={isDeleteAdditionalLoading}
        />
      )}
    </Box>
  );
};
